/* === ⚠️ THIS FILE CURRENTLY USES DEPRECATED PATTERNS ⚠️ === */
/* === 🔗 For more information visit https://go.inter.com/ember-best-practices 🔗 */
/* === 🚀 Please consider refactoring & removing some of the comments below when working on this file 🚀 */
/* eslint-disable no-restricted-imports */
/* RESPONSIBLE TEAM: team-inbox */
import { tracked } from '@glimmer/tracking';
import { trackedFunction } from 'ember-resources/util/function';
import { use } from 'ember-resources/util/function-resource';
import ENV from 'embercom/config/environment';
import containerLookup from 'embercom/lib/container-lookup';
import { request } from 'embercom/lib/inbox/requests';
import AppSettings, { type AppSettingsWireFormat } from 'embercom/objects/inbox/app-settings';
import type { AwayStatusReasonWireFormat } from 'embercom/objects/inbox/away-reason';
import { AwayStatusReason } from 'embercom/objects/inbox/away-reason';
import EmailSenderSettings from 'embercom/objects/inbox/email-sender-settings';
import ExternalPlan, { type ExternalPlanWireFormat } from 'embercom/objects/inbox/external-plan';
import { TicketType, type TicketTypeWireFormat } from 'embercom/objects/inbox/ticket';
import { type InserterId } from 'embercom/objects/inbox/types/composer';
import { AsyncData } from 'embercom/resources/utils/async-data';
import moment from 'moment-timezone';
import { get } from '../lib/ajax';
import type FeatureFlag from './feature-flag';
import AdminSummary, { type AdminSummaryWireFormat } from './inbox/admin-summary';
import ConversationAttributeDescriptor, {
  type ConversationAttributeDescriptorWireFormat,
} from './inbox/conversation-attribute-descriptor';
import TeamSummary, { type TeamSummaryWireFormat } from './inbox/team-summary';

export type WorkspaceForList = {
  id: string;
  id_code: string;
  is_standalone_app: boolean;
  migrating_from_standalone_to_suite: boolean;
  name: string;
  parent_app_id: string;
};

export default class Workspace {
  id: string;
  adminAssignees?: AdminSummary[];
  teamAssignees?: TeamSummary[];
  conversationAttributeDescriptors?: ConversationAttributeDescriptor[];
  emailSenderSettings?: EmailSenderSettings;
  @tracked features: Array<string> = [];
  @tracked helpCenterLocale?: string = undefined;

  @tracked isTestWorkspace?: boolean;
  @tracked isDeveloperWorkspace?: boolean;
  @tracked parentAppId?: string;
  @tracked testAppId?: string;
  @tracked customerId?: string;
  @tracked companySize?: string;
  @tracked hasConsentedToFinIngestion?: boolean;
  @tracked consentedToFinIngestionAt?: Date;
  @tracked createdAt?: Date;
  @tracked signedUpWithSolutionId?: number;
  @tracked name?: string;
  @tracked inProductSalesExperience?: string;
  @tracked onPricing5?: boolean;
  @tracked hasActiveSubscription?: boolean;
  @tracked isFrozenForNonPayment?: boolean;
  @tracked locale?: string;
  @tracked permittedLocales?: string[];

  get forceSuiteVersion() {
    return containerLookup('service:suiteMigrationService').forceSuiteVersion;
  }

  @tracked private hasStandaloneSetting = false;

  setHasStandaloneSetting(value: boolean) {
    this.hasStandaloneSetting = value;
  }

  get isStandaloneApp() {
    return this.hasStandaloneSetting && !this.forceSuiteVersion;
  }

  get isFullSuiteApp() {
    return !this.hasStandaloneSetting;
  }

  get isMigratingFromStandaloneToSuite() {
    return this.hasStandaloneSetting && this.hasStandaloneToSuiteMigrationFeature;
  }

  get hasStandaloneToSuiteMigrationFeature() {
    // we currently leave this feature flag enabled during and after the migration to suite
    return this.isFeatureEnabled('team-standalone-migrating-from-standalone-to-suite');
  }

  @tracked standalonePlatforms: Array<{ platform_type: string }> = [];
  @tracked aiInboxTranslationsEnabled = false;
  @tracked customerHasSucceedingSubscription?: boolean;
  @tracked customerSubscriptionWillBeCancelledAt?: Date;
  @tracked isSalesforceContracted?: boolean;
  @tracked inTrial?: boolean;
  @tracked hasSlackIntegration = false;
  @tracked standaloneCopilotFrameEnabled = false;
  private ticketTypesPromise?: Promise<TicketType[]>;
  private awayStatusReasonPromise?: Promise<AwayStatusReason[]>;
  private hasLoadedRequiredAttributes = false;
  private appSettings?: AppSettings;

  @use appSettingsLoader = AsyncData<AppSettings>(async () => {
    return await this.fetchAppSettings();
  });

  constructor(id: string) {
    this.id = id;
  }

  hasStandalonePlatform(platformName: string) {
    return this.standalonePlatforms.some((platform) => platform.platform_type === platformName);
  }

  setHasConsentedToFinIngestion(value: boolean) {
    this.hasConsentedToFinIngestion = value;
  }

  get isIntercom() {
    let regionService = containerLookup('service:regionService');
    return this.id === regionService.intercomAppCode;
  }

  isFin4All() {
    return this.id === 'fin4all';
  }

  isFeatureEnabled(feature: string) {
    return this.features.includes(feature);
  }

  // for compatibility with the old app service
  canUseFeature(feature: string) {
    return this.isFeatureEnabled(feature);
  }

  get canUseMBFAiAgentIdentities() {
    return this.isFeatureEnabled('mbf-ai-agent-identities');
  }

  get canUseMultiBrandGA() {
    return this.isFeatureEnabled('multi-brand-for-fin-ga');
  }

  get canUseAudiencesInPreview() {
    return this.isFeatureEnabled('fin-preview-audience');
  }

  get canSeeAiCsatMetrics() {
    return this.isFeatureEnabled('ai-insights-ai-csat-metrics');
  }

  get canUseReactFreshdeskDeployPages() {
    return this.isFeatureEnabled('team-standalone-react-freshdesk-deploy-pages');
  }

  /**
   * Whether all standalone platforms can use the React integration/deploy pages.
   *
   * Returns true when none of the app's standalone platforms are in the exclusion list.
   * When false, the app falls back to Ember deploy routes and static nav items (navbar.ts).
   *
   * IMPORTANT: Only add a platform here if it genuinely lacks React deploy page support.
   * Platforms like Freshdesk already have full React deploy pages — gating them behind
   * a FF here would regress them to the legacy Ember deploy UI (Connect/Workflow/Go live).
   * Use separate flags (e.g. canUseStandaloneConnectPage) for nav restructure concerns.
   */
  get canStandalonePlatformAccessReactIntegrationPages() {
    let platformsNotUsingReactUI = ['zendesk', 'api'];

    return (
      this.standalonePlatforms?.every(
        (platform) => !platformsNotUsingReactUI.includes(platform.platform_type),
      ) ?? false
    );
  }

  get canUseExtractionExternalSources() {
    return this.isFeatureEnabled('conversation-extraction-external-sources');
  }

  get canSeeCxScore() {
    return (
      this.isFeatureEnabled('cx_score') ||
      this.isFeatureEnabled('team-self-serve-ai-insights-addon')
    );
  }

  get canSeeOptimize() {
    return (
      this.isFeatureEnabled('optimize') ||
      this.isFeatureEnabled('team-self-serve-ai-insights-addon')
    );
  }

  get canSeeSuggestions() {
    return (
      this.isFeatureEnabled('suggestions') ||
      this.isFeatureEnabled('team-self-serve-ai-insights-addon')
    );
  }

  get canDisplayExpiredSubscriptionPage() {
    /**
     * To speed up sign-up, subscription creation now runs asynchronously. When the app loads, the expired subscription page is shown
     * as if the subscription is inactive. This logic prevents showing the expired subscription page for newly created apps (within the last 2 minutes),
     * ensuring enough time for the subscription to be set up before enforcing paywalls.
     */
    if (this.createdAt && moment().diff(moment(this.createdAt), 'minutes') <= 2) {
      return false;
    }
    return (
      this.envAllowsExpiredSubscriptionPage &&
      (!this.hasActiveSubscription || this.isFrozenForNonPayment)
    );
  }

  // By default the expired subscription page is disabled in development environment - to enable it enable the feature flag team-purchase-experience-expired-subscription-page-enabled-for-dev
  private get envAllowsExpiredSubscriptionPage() {
    return ENV.environment !== 'development';
  }

  get hasAccessToWorkflows() {
    return this.isFeatureEnabled('workflows_core_billing_feature');
  }

  get canUseInboxSearchInViews() {
    return this.isFeatureEnabled('inbox-search-in-views');
  }

  get canManageDeviceTokens() {
    return this.isFeatureEnabled('team-messenger-manage-device-tokens');
  }

  get isReactInboxKillswitchEnabled() {
    return this.isFeatureEnabled('react-inbox-killswitch');
  }

  get canUseReactInboxConversationStream() {
    return (
      !this.isReactInboxKillswitchEnabled &&
      this.isFeatureEnabled('react-inbox-conversation-stream')
    );
  }

  get canUseReactInboxStreamToggleControls() {
    return (
      !this.isReactInboxKillswitchEnabled &&
      this.isFeatureEnabled('react-inbox-stream-toggle-controls')
    );
  }

  get canUseReactInboxComposer() {
    return !this.isReactInboxKillswitchEnabled && this.isFeatureEnabled('react-inbox-composer');
  }

  get canUseReactInboxConversationHeader() {
    return (
      !this.isReactInboxKillswitchEnabled &&
      this.isFeatureEnabled('react-inbox-conversation-header')
    );
  }

  get canUseReactInboxRighthandSidebar() {
    return (
      !this.isReactInboxKillswitchEnabled && this.isFeatureEnabled('react-inbox-righthand-sidebar')
    );
  }

  get externalLinkShieldEnabled() {
    return this.useTrustedDomainsForUrlVerification;
  }

  /**
   @deprecated fetchAdminAssignees is deprecated. We are trying to move away from
    loading all data upfront. Please use the quick search APIs to load admins, if
    possible. If not, please drop a message in #team-inbox-eng.
   */
  async fetchAdminAssignees(): Promise<AdminSummary[]> {
    if (!this.adminAssignees) {
      let response = await request(`/ember/inbox/assignees/admins?app_id=${this.id}`);
      let json = (await response.json()).admins as AdminSummaryWireFormat[];
      this.adminAssignees = json.map(AdminSummary.deserialize);
    }

    return this.adminAssignees;
  }

  /**
   @deprecated fetchTeamAssignees is deprecated. We are trying to move away from
    loading all data upfront. Please use the quick search APIs to load teams, if
    possible. If not, please drop a message in #team-inbox-eng.
   */
  async fetchTeamAssignees(): Promise<TeamSummary[]> {
    if (!this.teamAssignees) {
      let response = await request(`/ember/inbox/assignees/teams?app_id=${this.id}`);
      let json = (await response.json()).teams as TeamSummaryWireFormat[];
      this.teamAssignees = json.map(TeamSummary.deserialize);
    }

    return this.teamAssignees;
  }

  @use conversationDataAttributeDescriptors = AsyncData<ConversationAttributeDescriptor[]>(
    async () => {
      return await this.fetchConversationAttributeDescriptors();
    },
  );

  async fetchConversationAttributeDescriptors(): Promise<ConversationAttributeDescriptor[]> {
    if (!this.conversationAttributeDescriptors) {
      let response = await request(`/ember/conversation_attributes/descriptors?app_id=${this.id}`);
      let json = (await response.json()) as ConversationAttributeDescriptorWireFormat[];
      let attributes = json.map(ConversationAttributeDescriptor.deserialize);
      this.conversationAttributeDescriptors = attributes;
    }

    return this.conversationAttributeDescriptors;
  }

  async fetchFeatures() {
    let response = await get(`/ember/apps/${this.id}.json?attribute_allowlist=features`);
    this.features = response.features.map((featureModel: FeatureFlag) => featureModel.id);
  }

  async fetchRequiredAttributes({ forceReload }: { forceReload?: boolean } = {}) {
    if (this.hasLoadedRequiredAttributes && !forceReload) {
      return;
    }

    // Don't forget to update`mockGetWorkspaceAttributes` function in tests/helpers/workspace.ts when adding new attributes
    let response = await get(
      `/ember/apps/${this.id}.json?attribute_allowlist=features,is_test_app,parent_app_id,test_app_id,customer_id,company_size,created_at,signed_up_with_solution_id,developer_workspace,name,has_consented_to_fin_ingestion,in_product_sales_experience,on_pricing5,has_active_subscription,is_frozen_for_non_payment,locale,permitted_locales,is_standalone_app,ai_inbox_translations_enabled,customer_has_succeeding_subscription,customer_subscription_will_be_cancelled_at,is_salesforce_contracted,in_trial,standalone_platforms,has_slack_integration`,
    );
    this.features = response.features.map((featureModel: FeatureFlag) => featureModel.id);
    this.testAppId = response.test_app_id;
    this.isTestWorkspace = response.is_test_app;
    this.isDeveloperWorkspace = response.developer_workspace;
    this.parentAppId = response.parent_app_id;
    this.customerId = response.customer_id;
    this.companySize = response.company_size;
    this.hasConsentedToFinIngestion = response.has_consented_to_fin_ingestion;
    this.consentedToFinIngestionAt = new Date(response.consented_to_fin_ingestion_at);
    this.createdAt = new Date(response.created_at);
    this.signedUpWithSolutionId = response.signed_up_with_solution_id;
    this.name = response.name;
    this.inProductSalesExperience = response.in_product_sales_experience;
    this.onPricing5 = response.on_pricing5;
    this.hasActiveSubscription = response.has_active_subscription;
    this.isFrozenForNonPayment = response.is_frozen_for_non_payment;
    this.locale = response.locale;
    this.permittedLocales = response.permitted_locales;
    this.hasStandaloneSetting = response.is_standalone_app;
    this.standalonePlatforms = response.standalone_platforms;
    this.aiInboxTranslationsEnabled = response.ai_inbox_translations_enabled;
    this.customerHasSucceedingSubscription = response.customer_has_succeeding_subscription;
    this.customerSubscriptionWillBeCancelledAt = response.customer_subscription_will_be_cancelled_at
      ? new Date(response.customer_subscription_will_be_cancelled_at)
      : undefined;
    this.isSalesforceContracted = response.is_salesforce_contracted;
    this.inTrial = response.in_trial;
    this.hasSlackIntegration = !!response.has_slack_integration;
    this.hasLoadedRequiredAttributes = true;
  }

  async fetchHelpCenterLocale() {
    if (this.appSettingsLoader.value) {
      this.helpCenterLocale = this.appSettingsLoader.value.helpCenterLocale;
    } else {
      this.helpCenterLocale = (await this.fetchAppSettings()).helpCenterLocale;
    }
  }

  async fetchEmailSenderSettings() {
    if (!this.emailSenderSettings) {
      let response = await get(`/ember/email_sender_settings?app_id=${this.id}`);
      this.emailSenderSettings = EmailSenderSettings.deserialize(response);
    }
    return this.emailSenderSettings;
  }

  get products() {
    return this.productsLoader.value ?? [];
  }

  private productsLoader = trackedFunction(this, async () => {
    let response = await get(`/ember/apps/${this.id}.json?attribute_allowlist=products`);
    return response.products ?? [];
  });

  get plans() {
    return this.plansLoader.value ?? [];
  }

  private plansLoader = trackedFunction(this, async () => {
    let json = (await get(`/ember/apps/${this.id}.json?attribute_allowlist=plans`)) as {
      plans: ExternalPlanWireFormat[];
    };

    return (json.plans ?? []).map((plan) => ExternalPlan.deserialize(plan));
  });

  get hasLightStandaloneInbox() {
    if (!this.isStandaloneApp) {
      return false;
    }

    return !(
      this.standaloneCopilotFrameEnabled || this.isFeatureEnabled('team-standalone-full-inbox')
    );
  }

  get disabledInserters() {
    if (this.appSettingsLoader.value) {
      return this.appSettingsLoader.value.disabledInserters;
    } else {
      return ['gifs'];
    }
  }

  get showTeammatesPresence() {
    if (this.appSettingsLoader.value) {
      return this.appSettingsLoader.value.showTeammatesPresence;
    } else {
      return false;
    }
  }

  get teamMentionsDisabled() {
    if (this.appSettingsLoader.value) {
      return this.appSettingsLoader.value.teamMentionsDisabled;
    } else {
      return false;
    }
  }

  get ticketStateFiltering() {
    if (this.appSettingsLoader.value) {
      return this.appSettingsLoader.value.ticketStateFiltering;
    } else {
      return false;
    }
  }

  get canFinUseConversationContent() {
    if (this.appSettingsLoader.value) {
      return this.appSettingsLoader.value.finUseInboxConversationContent;
    } else {
      return false;
    }
  }

  get isAnyChannelInstalled() {
    if (this.appSettingsLoader.value) {
      return this.appSettingsLoader.value.isAnyChannelInstalled;
    } else {
      return true;
    }
  }

  get hasCustomerTickets() {
    if (this.appSettingsLoader.value) {
      return this.appSettingsLoader.value.hasCustomerTickets;
    } else {
      return false;
    }
  }

  get hasBackOfficeTickets() {
    if (this.appSettingsLoader.value) {
      return this.appSettingsLoader.value.hasBackOfficeTickets;
    } else {
      return false;
    }
  }

  get hasTrackerTickets() {
    if (this.appSettingsLoader.value) {
      return this.appSettingsLoader.value.hasTrackerTickets;
    } else {
      return false;
    }
  }

  get disabledEmailConversationHistory() {
    if (this.appSettingsLoader.value) {
      return this.appSettingsLoader.value.emailRemoveConversationHistory;
    } else {
      return false;
    }
  }

  get attachUploadsInline() {
    if (this.appSettingsLoader.value) {
      return this.appSettingsLoader.value.attachUploadsInline;
    } else {
      return false;
    }
  }

  get useTrustedDomainsForUrlVerification() {
    if (this.appSettingsLoader.value) {
      return this.appSettingsLoader.value.useTrustedDomainsForUrlVerification;
    } else {
      return true;
    }
  }

  get trustedDomainsAndUrls() {
    if (this.appSettingsLoader.value) {
      return this.appSettingsLoader.value.trustedItems;
    } else {
      return [];
    }
  }

  get trustedDomains() {
    if (this.appSettingsLoader.value) {
      return this.appSettingsLoader.value.trustedDomains;
    } else {
      return [];
    }
  }

  get maliciousLinks() {
    if (this.appSettingsLoader.value) {
      return this.appSettingsLoader.value.maliciousLinks;
    } else {
      return [];
    }
  }

  get blockedDomainsAndUrls() {
    if (this.blockedDomainsAndUrlsEnabled && this.appSettingsLoader.value) {
      return this.appSettingsLoader.value.blockedItems;
    } else {
      return [];
    }
  }

  get enabledCrossUserConversationMerge() {
    if (this.appSettingsLoader.value) {
      return this.appSettingsLoader.value.enabledCrossUserConversationMerge;
    } else {
      return false;
    }
  }

  get adminAwayReasonIsMandatory() {
    if (this.appSettingsLoader.value) {
      return this.appSettingsLoader.value.adminAwayReasonIsMandatory;
    } else {
      return false;
    }
  }

  get blockedDomainsAndUrlsEnabled() {
    return this.isFeatureEnabled('app-settings-blocked-domains-and-urls');
  }

  async fetchAppSettings(): Promise<AppSettings> {
    if (!this.appSettings) {
      let response = await get(`/ember/inbox/app_settings.json?app_id=${this.id}`);
      this.appSettings = AppSettings.deserialize(response as AppSettingsWireFormat);
    }
    return this.appSettings;
  }

  @use pinnedComposerInserters = AsyncData<InserterId[]>(async () => {
    let response = await request(`/ember/admins/visible_composer_inserters.json?app_id=${this.id}`);
    let { inserters } = (await response.json()) as { inserters: InserterId[] };
    return inserters || [];
  });

  @use pinnedComposerSendCloseButton = AsyncData<boolean>(async () => {
    let response = await request(
      `/ember/admins/visible_composer_send_close.json?app_id=${this.id}`,
    );
    let { send_close_pinned } = (await response.json()) as { send_close_pinned: boolean };
    return send_close_pinned;
  });

  @use ticketTypes = AsyncData<TicketType[]>(async () => {
    return await this.fetchTicketTypes();
  });

  // Prefer the use of the ticketTypes resource, but there are lots of instances of
  // places that expect a promise & load ticket types in a task
  async fetchTicketTypes() {
    if (this.ticketTypesPromise) {
      return this.ticketTypesPromise;
    }

    return (this.ticketTypesPromise = (async () => {
      let response = await request(`/ember/inbox/ticket_types?app_id=${this.id}`);
      let json = (await response.json()) as TicketTypeWireFormat[];
      let ticketTypes = json.map((requestType) => TicketType.deserialize(requestType));
      return ticketTypes;
    })());
  }

  async fetchAwayStatusReasons(force = false) {
    if (!force && this.awayStatusReasonPromise) {
      return this.awayStatusReasonPromise;
    }

    return (this.awayStatusReasonPromise = (async () => {
      let response = await get('/ember/inbox/away_status_reasons', { app_id: this.id });
      let json = (await response) as AwayStatusReasonWireFormat[];

      return json.map((awayStatusReasonWireFormat: AwayStatusReasonWireFormat) =>
        AwayStatusReason.deserialize(awayStatusReasonWireFormat),
      );
    })());
  }

  resetTicketTypes() {
    this.ticketTypesPromise = undefined;
    this.ticketTypes.reload();
  }

  async reloadAppSettings() {
    this.appSettings = undefined;
    return this.appSettingsLoader.reload();
  }
}
